<template>
  <v-container fluid>
    <PageHeaderSlot>
      <template slot="top-right-slot" v-if="canCreate">
        <FormButton text="createProduct" isAdd class="ml-2" :to="{ name: 'CreateProduct' }" />
      </template>
    </PageHeaderSlot>

    <Datatable
      :isLoading="tableLoading"
      :tableHeaders="tableHeaders"
      :tableData="tableData"
      :page.sync="filter.page"
      :pageLimit.sync="filter.limit"
      :itemTotal="tableItemTotal"
      enableSearch
      @search-clicked="searchKeyword($event)"
      @search-input="filter.keyword = $event"
      enableClearAll
      @all-search-cleared="clearSearch()"
      @refresh-clicked="refreshData()"
      enableRefresh
      @options-update="getProductData()"
      actionViewRouteLink="ProductDetails"
      tableEmptyText="noProduct"
      enableMultiTypeSearch
      :searchTypeOptions="searchTypeOptions"
      :searchType.sync="filter.searchType"
      :initSearchKeyword="$route.query.search"
    >
      <template slot="append-toolbar-left">
        <FormSelect
          :isLoading="filterLoading"
          :fieldValue.sync="filter.category"
          :options="categoryOptions"
          hideDetails
          placeholder="searchCategory"
          enableClear
          @changed="searchCategory($event)"
          class="mr-3 mt-2 mb-3 search-select-field"
          enableTooltip
          :readonly="tableLoading"
        />
      </template>
    </Datatable>
  </v-container>
</template>

<script>
import Datatable from '@/components/Datatable.vue'
import FormSelect from '@/components/formField/FormSelect.vue'

export default {
  name: 'ProductList',
  components: {
    Datatable,
    FormSelect,
  },
  computed: {
    tableHeaders() {
      const vm = this
      let headers = [
        { value: 'product_code', text: vm.$t('productCode') },
        { value: 'product_name', text: vm.$t('product') },
        { value: 'product_category', text: vm.$t('category') },
        { value: 'inventory', text: vm.$t('inventory') },
        { value: 'unit_price', text: vm.$t('price') },
        { value: 'cost', text: vm.$t('cost') },
      ]
      if (this.enablePoints) {
        headers.push({ value: 'points', text: vm.$t('neededPoints') })
      }

      if (this.$validate.allowAction('ProductDetails', this.userType)) {
        headers.push({ value: 'actionView', text: '', align: 'end' })
      }
      return headers
    },
    // permission & setting
    canCreate() {
      return this.$validate.allowAction('CreateProduct', this.userType)
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),
    tableLoading: false,
    tableData: [],
    tableItemTotal: 0,
    filter: {
      category: null,
      page: 1,
      limit: 10,
      keyword: '',
      searchType: 'product_code',
    },
    filterLoading: false,
    categoryOptions: [],

    enablePoints: false,

    // -- static
    searchTypeOptions: [
      { value: 'product_code', text: vm.$t('productCode') },
      { value: 'name', text: vm.$t('productName') },
    ],
  }),
  methods: {
    async getProductData(regenUrl = true) {
      if (regenUrl === true) {
        this._regenerateURL()
      }

      this.tableLoading = true
      try {
        let payload = {
          filter_limit: this.filter.limit,
          filter_page: this.filter.page - 1,
          join_category_data: true,
          filter_item: [],
          with_stock: '',
        }
        const selectedSchool = this.gfGetUserSelectedSchool()
        if (selectedSchool > -1) {
          payload['center_id'] = selectedSchool
        }

        if (this.$validate.DataValid(this.filter.keyword) && this.$validate.DataValid(this.filter.searchType)) {
          payload['filter_item'].push({ key: this.filter.searchType, value: this.filter.keyword.trim() })
        }

        if (this.$validate.DataValid(this.filter.category) && parseInt(this.filter.category) > -1) {
          payload['filter_item'].push({ key: 'category', value: parseInt(this.filter.category) })
        }

        const { data, total } = await this.$Fetcher.GetProducts(payload)
        this.tableData = data.map(el => {
          return {
            id: el.id,
            product_code: el.product_code,
            product_name: el.name,
            product_category: this.$validate.DataValid(el.category_data) ? el.category_data.name : '',
            inventory: el.inventory,
            unit_price: el.price,
            cost: el.avg_cost,
            points: el.points,
          }
        })

        this.tableItemTotal = total
      } catch (err) {
        this.$common.error(err)
        this.tableData = []
        if (this.filter.page === 1) {
          this.tableItemTotal = 0
        }
      } finally {
        this.tableLoading = false
      }
    },
    _search() {
      this.filter.page = 1
      this.tableItemTotal = 0
      this.getProductData()
    },
    clearFilter() {
      this.filter.page = 1
      this.tableItemTotal = 0
      this.filter.keyword = ''
      this.filter.searchType = this.searchTypeOptions[0].value
      this.filter.category = null
    },
    searchCategory(val) {
      if (this.$validate.DataValid(val)) {
        this.filter.category = parseInt(val)
      } else {
        this.filter.category = null
      }
      this._search()
    },
    searchKeyword(keyword) {
      this.filter.keyword = keyword.trim()
      this._search()
    },
    clearSearch() {
      this.clearFilter()
      this._search()
    },
    refreshData() {
      this.filter.limit = 10
      this._search()
    },

    _getQuery() {
      this.clearFilter()
      this.filter.limit = 10

      const query = this.gfGetTableQuery({
        searchType: 'string',
        search: 'string',
        page: 'int',
        limit: 'int',
        c: 'int',
      })
      for (const key in this.filter) {
        if (key === 'keyword') {
          if (this.$validate.DataValid(query['search'])) {
            this.$set(this.filter, key, query['search'])
          }
        } else if (key === 'category') {
          if (this.$validate.DataValid(query['c'])) {
            this.$set(this.filter, key, query['c'])
          }
        } else if (this.$validate.DataValid(query[key])) {
          this.$set(this.filter, key, query[key])
        }
      }
    },
    _regenerateURL() {
      let q = {
        searchType: this.filter.searchType,
        search: this.filter.keyword.trim(),
        page: this.filter.limit !== 10 || this.filter.page !== 1 ? this.filter.page : null,
        limit: this.filter.limit !== 10 || this.filter.page !== 1 ? this.filter.limit : null,
        c: this.$validate.DataValid(this.filter.category) && this.filter.category > -1 ? this.filter.category : null,
      }
      if (this.$validate.DataValid(this.filter.category) && this.filter.category > -1) {
        const found = this.categoryOptions.find(el => el.value === parseInt(this.filter.category))
        if (found) q['category'] = found.text
      }
      const newQuery = this.$common.diffQuery(q, this.$route.query)
      if (newQuery) this.$router.replace({ query: newQuery })
    },

    async getCategoryOptions() {
      try {
        const { data } = await this.$Fetcher.GetProductCategories()
        this.categoryOptions = data.map(el => {
          return { value: el.id, text: el.name }
        })
      } catch (err) {
        if (err === 'abort') {
          return false
        }
      }
      return true
    },

    async getPointsSetting() {
      if (this.gEnablePointSystem) {
        try {
          const data = await this.$Fetcher.GetSetting()
          if (data.points_enable) this.enablePoints = true
        } catch (err) {
          this.$common.error(err)
          if (err === 'abort') {
            return false
          }
        }
      }

      return true
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: ProductList')
      this.userType = this.gfGetUserType()
      this._getQuery()
      this.$store.dispatch('toggleLoadingPage', false)

      let next = await this.getPointsSetting()
      if (next) next = await this.getCategoryOptions()
      if (next) await this.getProductData(false)
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.tableLoading = true
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    this.$XHR.abort()
    next()
  },
}
</script>
